import { MountElement, paymentIntent, PaymentIntentProps } from '@nemuru-eng/checkout-js';
import {
  A2AProviderName,
  Agent,
  Branding,
  CardPaymentProviderName,
  CheckoutSession,
  CheckoutSessionStatus,
  getCheckoutSessionById,
  LenderApprovalStatus,
  LenderName,
  LoanApplicationStatus,
  LoanStatusExternal,
  Locale,
  OnboardingFulfilledBy,
  patchSetSelectedPaymentIntent,
  PaymentIntent,
  PaymentMethodType,
  postCreatePaymentIntent,
} from '@nemuru-eng/js-api-client';
import { generatePath } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ApiError } from '@/components/modules/_shared/exceptions/ApiError';
import { AbstractPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { CheckoutSessionEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionEntity';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { AbstractProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/AbstractProgressWorkflow';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ProviderName } from '@/types/ProviderName';

export type LenderSelectedData = {
  lenderName: LenderName;
  lenderApprovalState: LoanApplicationStatus;
  lenderApprovalStatus: LenderApprovalStatus | undefined;
};

export abstract class PaymentMethodDetailEntity {
  private static readonly PAYMENT_METHOD_SINGLE_PAYMENT = [
    PaymentMethodType.CARD_PAYMENT,
    PaymentMethodType.A2A_PAYMENT,
  ];

  public static readonly PAYMENT_INTENT_STATUS_FINAL = [
    LoanStatusExternal.CONFIRMED,
    LoanStatusExternal.CAPTURED,
    LoanStatusExternal.PAID,
    LoanStatusExternal.DENIED,
    LoanStatusExternal.CANCELLED,
    LoanStatusExternal.DESISTED,
  ];

  private static readonly PAYMENT_INTENT_STATUS_WITH_MODIFY = [LoanStatusExternal.INITIAL];

  static readonly PROVIDER_NAMES_WITH_MODIFY: (LenderName | CardPaymentProviderName | A2AProviderName)[] = [
    LenderName.CAIXABANK,
    LenderName.BBVA,
  ];

  public readonly type: PaymentMethodType;
  public readonly agent: Agent;
  public paymentIntentId?: string;
  public paymentMethodId?: string;
  // private paymentIntentProviderName?: LenderName | A2AProviderName | CardPaymentProviderName | null; // TODO: incoming
  public status?: LoanStatusExternal;
  public actions: Action[] | undefined;
  public paymentMethod?: AbstractPaymentMethod;

  private _checkoutSession: CheckoutSession;
  private _error?: Error;
  private _mountElement?: MountElement;

  protected _isRefreshed = false;

  protected abstract _progress?: AbstractProgress | AbstractProgressWorkflow;
  protected abstract getProgress(): Promise<AbstractProgress | AbstractProgressWorkflow>;
  public abstract get progress(): AbstractProgress | AbstractProgressWorkflow | undefined;

  constructor(type: PaymentMethodType, checkoutSession: CheckoutSession, agent: Agent, paymentIntent?: PaymentIntent) {
    this.type = type;
    this._checkoutSession = checkoutSession;
    this.agent = agent;
    this.status = paymentIntent?.status;
    this.paymentMethodId = paymentIntent?.paymentMethodId;
    this.paymentIntentId = paymentIntent?.id;
    // this.paymentIntentProviderName = paymentIntent?.providerName; // TODO: incoming
  }

  public abstract clone(): this;
  public abstract refresh(): Promise<this>;
  public abstract isActionCompleted(action: Action): Promise<boolean>;

  public async createPaymentIntent(clientSecret: string): Promise<this> {
    if (this.shouldCreatePaymentIntentBesidesExternalProvider()) {
      try {
        const paymentMethodId = uuidv4();

        await postCreatePaymentIntent({
          paymentMethodId: paymentMethodId,
          checkoutSessionId: this.checkoutSession.id,
          paymentMethodType: this.type,
          clientSecret: clientSecret,
        });

        this._checkoutSession = await getCheckoutSessionById({ checkoutSessionId: this.checkoutSession.id });

        const paymentIntent = this._checkoutSession.paymentIntents.find(
          (pi) => pi.paymentMethodType === this.type && pi.paymentMethodId === paymentMethodId,
        );

        if (!paymentIntent) {
          throw new Error('Payment intent not found after creating payment intent');
        }

        this.paymentIntentId = paymentIntent.id;
        this.status = paymentIntent.status;
        this.paymentMethodId = paymentIntent.paymentMethodId;
      } catch (error) {
        throw new ApiError(error as Error);
      }
    }
    return this;
  }

  public reset(): this {
    this.paymentIntentId = undefined;
    this.paymentMethodId = undefined;
    this.status = undefined;
    this.actions = undefined;
    this._progress = undefined;
    this._isRefreshed = false;
    this._error = undefined;
    this._mountElement = undefined;
    return this;
  }

  public async setSelectedPaymentIntent(paymentIntent: PaymentIntent | undefined): Promise<this> {
    this.paymentIntentId = paymentIntent?.id;
    this.paymentMethodId = paymentIntent?.paymentMethodId;
    this.status = paymentIntent?.status;
    return this;
  }

  public async setSelected(clientSecret: string): Promise<void> {
    if (this.isSelected) return;
    if (!this.paymentMethodId) throw new Error('PaymentMethodId is not set');

    await patchSetSelectedPaymentIntent({
      id: this.checkoutSession.id,
      paymentMethodType: this.type,
      paymentMethodId: this.paymentMethodId,
      clientSecret,
    });
  }

  public mount(data: {
    queryString: string;
    clientSecret: string;
    onClose: (status: string) => void;
    onLenderSelected?: (data: LenderSelectedData) => void;
    onError?: (error: string) => void;
    locale?: Locale;
  }): void {
    if (!this.paymentMethodId) {
      throw new Error('PaymentMethodId is not set');
    }

    const onLenderSelected = (input: LenderSelectedData) => {
      if (!input.lenderName || !input.lenderApprovalStatus || !input.lenderApprovalState) {
        return;
      }

      this._mountElement?.unmount();
      this._mountElement = undefined;
      data.onLenderSelected?.(input);
    };

    const pi = paymentIntent({
      paymentMethodType: this.type,
      clientSecret: data.clientSecret,
      options: this.getPaymentIntentMountOptions(data.locale),
      onLenderSelected,
      onClose: data.onClose,
      onError: data.onError,
    });

    this._mountElement = pi;
    this._mountElement.mount(data.queryString);
  }

  protected getPaymentIntentMountOptions(locale?: Locale): PaymentIntentProps['options'] {
    return { locale: locale ?? Locale.ES_ES };
  }

  public paymentMethodUrl() {
    if (!this.paymentMethodId) return;

    const params = this.paymentMethodToUrl(this.type);
    if (!params) return;

    return generatePath(params.path, {
      [params.key]: this.paymentMethodId,
    });
  }

  private paymentMethodToUrl(paymentMethod: PaymentMethodType): { path: string; key: string } | null {
    const keys = {
      LOAN_ID: 'loanId',
      PAYMENT_METHOD_ID: 'paymentMethodId',
      CREDIT_CARD_ID: 'creditCardId',
    };

    switch (paymentMethod) {
      case PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY:
      case PaymentMethodType.CONSUMER_LOAN_INSTALMENTS:
      case PaymentMethodType.CARD_ONBOARDING:
        return { path: DYNAMIC_PAGE_PATHS.CONSUMER_LOAN, key: keys.LOAN_ID };
      case PaymentMethodType.AUTO_LOAN:
      case PaymentMethodType.B2B_LOAN:
        return { path: DYNAMIC_PAGE_PATHS.AUTO_LOAN, key: keys.LOAN_ID };
      case PaymentMethodType.CREDIT_LINE:
        return { path: DYNAMIC_PAGE_PATHS.CREDIT_LINE, key: keys.LOAN_ID };
      case PaymentMethodType.BNPL_SPLIT_PAY:
      case PaymentMethodType.BNPL_INSTALMENTS:
        return { path: DYNAMIC_PAGE_PATHS.ORDER, key: keys.LOAN_ID };
      case PaymentMethodType.CARD_PAYMENT:
        return { path: DYNAMIC_PAGE_PATHS.CARD_PAYMENT, key: keys.PAYMENT_METHOD_ID };
      case PaymentMethodType.A2A_PAYMENT:
        return { path: DYNAMIC_PAGE_PATHS.A2A_PAYMENT, key: keys.PAYMENT_METHOD_ID };
      case PaymentMethodType.CREDIT_CARD:
        return { path: DYNAMIC_PAGE_PATHS.CREDIT_CARD, key: keys.CREDIT_CARD_ID };
      default:
        return null;
    }
  }

  protected setError(error: Error | undefined): void {
    this._error = error;
  }

  public getPaymentIntents(): PaymentIntent[] {
    if (!this.paymentIntentId) return [];
    if (this.checkoutSession.paymentIntents.length === 0) return [];

    return this.checkoutSession.paymentIntents
      .filter((pi) => pi.paymentMethodType === this.type)
      .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
  }

  public getPaymentIntent(providerName?: ProviderName): PaymentIntent | undefined {
    const paymentIntents = this.getPaymentIntents();
    if (paymentIntents.length === 0) return;

    if (providerName) {
      return paymentIntents.find((pi) => pi.providerName === providerName);
    }
    return paymentIntents[0];
  }

  public shouldCreatePaymentIntent(): boolean {
    if (this.isCheckoutSessionFinal(this.checkoutSession.status)) return false;
    if (!this.isAgentAllowedToCompleteOnboarding()) return false;
    if (!this.paymentMethodId) return true;
    if (this.isExpired()) return true;
    return false;
  }

  public shouldCreatePaymentIntentBesidesExternalProvider(): boolean {
    const shouldCreate = this.shouldCreatePaymentIntent();
    if (shouldCreate) return true;
    const paymentIntentId = this.paymentIntentId;
    if (!paymentIntentId) return true;
    const paymentIntent = this.checkoutSession.paymentIntents.find((pi) => pi.id === paymentIntentId);
    if (!paymentIntent) return true;
    if (!paymentIntent.providerName) return false;
    return false;
  }

  public hasPaymentIntentNotInFinalStatus(providerNames: (ProviderName | null)[]): boolean {
    if (!this.checkoutSession.paymentIntents.length) return false;

    const paymentIntents = this.checkoutSession.paymentIntents.filter((pi) => {
      if (pi.paymentMethodType !== this.type) return false;
      if (this.isStatusFinal(pi.status)) return false;
      if (!providerNames.includes(pi.providerName)) return false;
      return true;
    });

    return paymentIntents.length > 0;
  }

  protected isCheckoutSessionFinal(status: CheckoutSessionStatus): boolean {
    return CheckoutSessionEntity.hasFinalStatus(status);
  }

  public isAgentAllowedToCompleteOnboarding(): boolean {
    return this.agent.allowedOnboardingFulfilledByTypes.includes(OnboardingFulfilledBy.MERCHANT_POS);
  }

  public static isSinglePayment(type: PaymentMethodType): boolean {
    return PaymentMethodDetailEntity.PAYMENT_METHOD_SINGLE_PAYMENT.includes(type);
  }

  private isStatusFinal(status: LoanStatusExternal): boolean {
    return PaymentMethodDetailEntity.PAYMENT_INTENT_STATUS_FINAL.includes(status);
  }

  public isDeniedOrCancelledOrExpired(): boolean {
    if (!this.status) return false;
    return (
      this.status === LoanStatusExternal.DENIED ||
      this.status === LoanStatusExternal.CANCELLED ||
      this.status === LoanStatusExternal.EXPIRED
    );
  }

  public isFinalStatus(): boolean {
    if (!this.status) return false;
    return this.isStatusFinal(this.status);
  }

  public isRequested(): boolean {
    return !!this.status && this.status !== LoanStatusExternal.INITIAL && !this.isFinalStatus();
  }

  public isConfirmed(): boolean {
    return !!this.status && this.status === LoanStatusExternal.CONFIRMED;
  }

  public isExpired(): boolean {
    return !!this.status && this.status === LoanStatusExternal.EXPIRED;
  }

  public isAllowedToModify(): boolean {
    if (!this.status) return true;
    if (!PaymentMethodDetailEntity.PAYMENT_INTENT_STATUS_WITH_MODIFY.includes(this.status)) return false;
    return true; // TODO: incoming
  }

  public requiresNieData(): boolean {
    return false;
  }

  public branding(): Branding {
    if (this.checkoutSession.options?.customization?.branding) {
      return this.checkoutSession.options.customization.branding as Branding;
    }
    return this.agent.customTheme as unknown as Branding;
  }

  public get checkoutSession(): CheckoutSession {
    return this._checkoutSession;
  }

  public get isSelected(): boolean {
    return this.type === this.checkoutSession.selectedPaymentMethodType;
  }

  public get isRefreshed(): boolean {
    return this._isRefreshed;
  }

  public get error(): Error | undefined {
    return this._error;
  }
}
